import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableEmpty,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/table'
import { SkeletonRows } from '@/components/ui/skeleton'
import { useOrganizations } from '@/features/workers/hooks'
import { useFichas, useEncounters } from '@/features/fichas/hooks'
import { TEMPLATE_OPTIONS } from '@/features/fichas/templates'
import type { FichaTemplate } from '@/features/fichas/types'
import { todayChile } from '@/lib/utils'

export default function FichasListPage() {
  const [organizationId, setOrganizationId] = useState<string>('')
  const [operativoId, setOperativoId] = useState<string>('')
  const [template, setTemplate] = useState<FichaTemplate | ''>('')
  const [from, setFrom] = useState<string>('')
  const [to, setTo] = useState<string>('')

  const { data: orgsData } = useOrganizations()
  const { data: encData } = useEncounters({
    organizationId: organizationId || undefined,
  })

  const { data, isLoading, isError, error } = useFichas({
    organizationId: organizationId || undefined,
    operativoId: operativoId || undefined,
    template: (template || undefined) as FichaTemplate | undefined,
    from: from || undefined,
    to: to || undefined,
  })

  const orgOptions = useMemo(
    () => [
      { value: '', label: 'Todas las empresas' },
      ...(orgsData?.items ?? []).map((o) => ({ value: o.id, label: o.name })),
    ],
    [orgsData],
  )

  const operativoOptions = useMemo(
    () => [
      { value: '', label: 'Todos los operativos' },
      ...(encData?.items ?? []).map((e) => ({ value: e.operativoId, label: e.operativoId })),
    ],
    [encData],
  )

  const templateOptions = [
    { value: '', label: 'Todos los templates' },
    ...TEMPLATE_OPTIONS.map((t) => ({ value: t.value, label: t.label })),
  ]

  return (
    <div className="flex flex-col gap-4" data-testid="fichas-page">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Fichas clínicas</h1>
          <p className="text-sm text-slate-600">
            Registro de evaluaciones por trabajador en cada operativo.
          </p>
        </div>
        <Link to="/app/fichas/nueva">
          <Button leftIcon={<Plus className="h-4 w-4" />} data-testid="nueva-ficha-btn">
            Nueva ficha
          </Button>
        </Link>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Select
          value={organizationId}
          onChange={setOrganizationId}
          options={orgOptions}
          placeholder="Empresa"
          data-testid="fichas-filter-org"
        />
        <Select
          value={operativoId}
          onChange={setOperativoId}
          options={operativoOptions}
          placeholder="Operativo"
          data-testid="fichas-filter-operativo"
        />
        <Select
          value={template}
          onChange={(v) => setTemplate(v as FichaTemplate | '')}
          options={templateOptions}
          placeholder="Template"
          data-testid="fichas-filter-template"
        />
        <Input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          max={todayChile()}
          placeholder="Desde"
          data-testid="fichas-filter-from"
        />
        <Input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          max={todayChile()}
          placeholder="Hasta"
          data-testid="fichas-filter-to"
        />
      </div>

      {isError && (
        <div
          role="alert"
          className="rounded-md border border-[color:var(--color-fitlab-red)]/30 bg-[color:var(--color-fitlab-red)]/5 p-3 text-sm text-[color:var(--color-fitlab-red-hover)]"
        >
          No se pudieron cargar las fichas: {error?.message ?? 'error desconocido'}.
        </div>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Fecha</TableHeaderCell>
              <TableHeaderCell>Trabajador</TableHeaderCell>
              <TableHeaderCell>Template</TableHeaderCell>
              <TableHeaderCell>Empresa</TableHeaderCell>
              <TableHeaderCell>Creada por</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows rows={6} cols={5} />
            ) : !data?.items.length ? (
              <tr>
                <td colSpan={5}>
                  <TableEmpty>
                    <p className="font-medium text-slate-700">
                      Sin fichas en los filtros actuales.
                    </p>
                    <p>Ajusta los filtros o crea una nueva ficha.</p>
                  </TableEmpty>
                </td>
              </tr>
            ) : (
              data.items.map((f) => (
                <TableRow key={f.id}>
                  <TableCell>
                    <Link
                      to={`/app/fichas/${encodeURIComponent(f.id)}`}
                      className="text-[color:var(--color-fitlab-blue)] hover:underline"
                      data-testid={`ficha-link-${f.id}`}
                    >
                      {new Date(f.created_at).toLocaleDateString('es-CL', {
                        timeZone: 'America/Santiago',
                      })}
                    </Link>
                  </TableCell>
                  <TableCell>{f.worker_name ?? f.worker_id}</TableCell>
                  <TableCell>
                    <Badge
                      tone={
                        f.template === 'laboratorio'
                          ? 'danger'
                          : f.template === 'antropometria'
                            ? 'success'
                            : 'info'
                      }
                    >
                      {f.template}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-slate-600">{f.organization_name ?? '—'}</TableCell>
                  <TableCell className="text-slate-600">
                    {f.created_by_name ?? f.created_by}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}
